// data-fit LLC · DecisionFlow · ADMIN Stage
// AdminFlowShell.jsx · DDL: R5 · API: v1.11 · Session 93
// Auth: useAuth() direct in child screens — ARCH-2 compliant
// Key constraints: SCREEN_ORDER exported; child navigation via CustomEvent; ADMIN-07 excluded from navigation
// Open Items: OI-ADMIN-ROLLFORWARD-ENDPOINT
// Patches: PATCH-S93-SHELL-01 (state-during-render anti-pattern fixed), PATCH-S93-SHELL-02 (invalid screen warn added)
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*

import React, { useEffect, useState } from 'react'
import { useFeatures } from '../../auth/FeatureContext.jsx'

import FiscalPeriodManager from './FiscalPeriodManager'
import FiscalYearManager from './FiscalYearManager'
import ParameterConfigEditor from './ParameterConfigEditor'
import ImportLogViewer from './ImportLogViewer'
import AuditLogViewer from './AuditLogViewer'
import NotificationCenter from './NotificationCenter'
import NotificationPreferences from './NotificationPreferences'
import PeriodRollForward from './PeriodRollForward'
import FXRateManager from './FXRateManager'
import ScenarioSetBuilder from './ScenarioSetBuilder'

export const SCREEN_ORDER = [
  'FiscalPeriodManager',
  'FiscalYearManager',
  'ParameterConfigEditor',
  'ImportLogViewer',
  'AuditLogViewer',
  'NotificationCenter',
  'NotificationPreferences',
  'FXRateManager',
  'ScenarioSetBuilder',
]

const SCREEN_LABELS = {
  FiscalPeriodManager: 'Fiscal Periods',
  FiscalYearManager: 'Fiscal Years',
  ParameterConfigEditor: 'Parameters',
  ImportLogViewer: 'Import Logs',
  AuditLogViewer: 'Audit Logs',
  NotificationCenter: 'Notifications',
  NotificationPreferences: 'Notification Preferences',
  FXRateManager: 'FX Rates',
  ScenarioSetBuilder: 'Scenario Sets',
}

const AI_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
}

export default function AdminFlowShell({ fiscalPeriodId, wizardSessionId }) {
  const { hasFeature } = useFeatures()

  const [aiStatus, setAiStatus] = useState(AI_STATUS.IDLE)
  const [aiResponse, setAiResponse] = useState(null)
  const [currentScreen, setCurrentScreen] = useState(SCREEN_ORDER[0])
  const [showRollForward, setShowRollForward] = useState(false)

  // Navigation event listener — handles child-initiated screen changes
  useEffect(() => {
    const handleDecisionFlowNavigation = (event) => {
      const requestedScreen = event?.detail?.screen

      if (SCREEN_ORDER.includes(requestedScreen)) {
        setCurrentScreen(requestedScreen)
      } else if (requestedScreen) {
        // PATCH-S93-SHELL-02: warn on invalid screen name to aid dev handoff debugging
        console.warn(`[AdminFlowShell] Invalid screen requested: ${requestedScreen}`)
      }
    }

    window.addEventListener('decisionflow', handleDecisionFlowNavigation)

    return () => {
      window.removeEventListener('decisionflow', handleDecisionFlowNavigation)
    }
  }, [])

  // PATCH-S93-SHELL-01: fallback reset moved out of render into useEffect
  useEffect(() => {
    if (!SCREEN_ORDER.includes(currentScreen)) {
      setCurrentScreen(SCREEN_ORDER[0])
    }
  }, [currentScreen])

  const onNavigate = (screen) => {
    window.dispatchEvent(
      new CustomEvent('decisionflow', {
        detail: { screen },
      })
    )
  }

  const handleTabClick = (index) => {
    setCurrentScreen(SCREEN_ORDER[index])
  }

  const childProps = {
    aiStatus,
    aiResponse,
    currentScreen,
    onNavigate,
  }

  const renderCurrentScreen = () => {
    if (!SCREEN_ORDER.includes(currentScreen)) {
      return null
    }

    switch (currentScreen) {
      case 'FiscalPeriodManager':
        return <FiscalPeriodManager {...childProps} />
      case 'FiscalYearManager':
        return <FiscalYearManager {...childProps} />
      case 'ParameterConfigEditor':
        return <ParameterConfigEditor {...childProps} />
      case 'ImportLogViewer':
        return <ImportLogViewer {...childProps} />
      case 'AuditLogViewer':
        return <AuditLogViewer {...childProps} />
      case 'NotificationCenter':
        return <NotificationCenter {...childProps} />
      case 'NotificationPreferences':
        return <NotificationPreferences {...childProps} />
      case 'FXRateManager':
        if (!hasFeature('feature.tier.fx_rate_management')) return null;
        return <FXRateManager {...childProps} fiscalPeriodId={fiscalPeriodId} wizardSessionId={wizardSessionId} />
      case 'ScenarioSetBuilder':
        if (!hasFeature('feature.tier.scenario_planning')) return null;
        return <ScenarioSetBuilder {...childProps} fiscalPeriodId={fiscalPeriodId} wizardSessionId={wizardSessionId} />
      default:
        return null
    }
  }

  const renderAiPanel = () => {
    if (aiStatus === AI_STATUS.LOADING) {
      return (
        <div className="mt-2 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3">
          <h3 className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">AI Assistant</h3>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-primary))]">Analyzing admin context…</p>
        </div>
      )
    }

    if (aiStatus === AI_STATUS.READY) {
      return (
        <div className="mt-2 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3">
          <h3 className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">AI Assistant</h3>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">{aiResponse || 'No recommendation returned.'}</p>
        </div>
      )
    }

    if (aiStatus === AI_STATUS.ERROR) {
      return (
        <div className="mt-2 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3">
          <h3 className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">AI Assistant</h3>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-danger))]">Unable to load AI guidance for this admin screen.</p>
        </div>
      )
    }

    return (
      <div className="mt-2 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3">
        <h3 className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">AI Assistant</h3>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">AI guidance will appear here when available.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">ADMIN Stage</h2>
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">Manage fiscal setup, parameters, imports, audit history, and notifications.</p>
        </div>
        <button
          type="button"
          className="shrink-0 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))] transition-colors"
          onClick={() => setShowRollForward((currentValue) => !currentValue)}
        >
          {showRollForward ? 'Hide Roll-Forward' : 'Roll-Forward Status'}
        </button>
      </div>

      <nav className="flex flex-wrap gap-0 border-b border-[rgb(var(--color-border))]" aria-label="Admin stage screens">
        {SCREEN_ORDER.filter((screenName) => {
          if (screenName === 'FXRateManager'     && !hasFeature('feature.tier.fx_rate_management')) return false;
          if (screenName === 'ScenarioSetBuilder' && !hasFeature('feature.tier.scenario_planning'))  return false;
          return true;
        }).map((screenName, index) => (
          <button
            key={screenName}
            type="button"
            className={`px-4 py-2 text-tok-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              currentScreen === screenName
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
            }`}
            onClick={() => handleTabClick(index)}
            aria-current={currentScreen === screenName ? 'page' : undefined}
          >
            {SCREEN_LABELS[screenName]}
          </button>
        ))}
      </nav>

      <div>{renderCurrentScreen()}</div>

      {showRollForward && (
        <div className="mt-2 border-t border-[rgb(var(--color-border))] pt-4">
          <PeriodRollForward />
        </div>
      )}

      {renderAiPanel()}
    </div>
  )
}
